/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { AddSubscriptionRequest } from '../models/add-subscription-request';
import { AddSubscriptionResponse } from '../models/add-subscription-response';
import { InvoicePresence } from '../models/invoice-presence';
import { UpdateSubscriptionRequest } from '../models/update-subscription-request';


/**
 * Server-sent event stream and push-notification subscriptions,
 * including per-invoice presence.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationsService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation subscribeNotifications
   */
  static readonly SubscribeNotificationsPath = '/notifications/subscribe';

  /**
   * Open a Server-Sent Events stream for this browser tab.
   *
   * Establishes the per-tab SSE connection identified by
   * `page_instance_id`. Entity subscriptions are attached separately
   * via `POST /notifications/subscriptions`.
   *
   * Reconnecting with the same `page_instance_id` as the same user
   * reopens that tab's connection. A `page_instance_id` that names
   * another user's connection is refused with 403 and leaves that
   * connection as it was.
   *
   * The stream carries discriminated event messages keyed by
   * `event_type`. Possible values and their payload schemas:
   *   - `heartbeat`                  → HeartbeatEvent
   *   - `text_message_updated`       → TextMessageUpdatedEvent
   *   - `email_created`              → EmailCreatedEvent
   *   - `email_updated`              → EmailUpdatedEvent
   *   - `payment_status_updated`     → PaymentStatusUpdatedEvent
   *   - `payment_created`            → PaymentCreatedEvent
   *   - `payment_history_created`    → PaymentHistoryCreatedEvent
   *   - `client_updated`             → ClientUpdatedEvent
   *   - `end_customer_updated`       → EndCustomerUpdatedEvent
   *   - `invoice_attachment_created` → InvoiceAttachmentCreatedEvent
   *   - `invoice_attachment_updated` → InvoiceAttachmentUpdatedEvent
   *   - `invoice_updated`            → InvoiceUpdatedEvent
   *   - `comment_changed`            → CommentChangedEvent
   *   - `invoice_presence_changed`   → InvoicePresenceChangedEvent
   *   - `invoice_workflow_activity`  → InvoiceWorkflowActivityEvent
   *   - `workflow_status`            → WorkflowStatusEvent
   *   - `export_changed`             → ExportChangedEvent
   *   - `extraction_changed`         → ExtractionChangedEvent
   *
   * OpenAPI cannot model `text/event-stream` as a typed channel, so
   * the response schema stays `{ type: string }`. Clients SHOULD
   * discriminate on `event_type` and SHOULD gracefully ignore unknown
   * values to remain forward-compatible.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `subscribeNotifications()` instead.
   *
   * This method doesn't expect any request body.
   */
  subscribeNotifications$Response(params: {
    page_instance_id: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<string>> {

    const rb = new RequestBuilder(this.rootUrl, NotificationsService.SubscribeNotificationsPath, 'get');
    if (params) {
      rb.query('page_instance_id', params.page_instance_id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: 'text/event-stream',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<string>;
      })
    );
  }

  /**
   * Open a Server-Sent Events stream for this browser tab.
   *
   * Establishes the per-tab SSE connection identified by
   * `page_instance_id`. Entity subscriptions are attached separately
   * via `POST /notifications/subscriptions`.
   *
   * Reconnecting with the same `page_instance_id` as the same user
   * reopens that tab's connection. A `page_instance_id` that names
   * another user's connection is refused with 403 and leaves that
   * connection as it was.
   *
   * The stream carries discriminated event messages keyed by
   * `event_type`. Possible values and their payload schemas:
   *   - `heartbeat`                  → HeartbeatEvent
   *   - `text_message_updated`       → TextMessageUpdatedEvent
   *   - `email_created`              → EmailCreatedEvent
   *   - `email_updated`              → EmailUpdatedEvent
   *   - `payment_status_updated`     → PaymentStatusUpdatedEvent
   *   - `payment_created`            → PaymentCreatedEvent
   *   - `payment_history_created`    → PaymentHistoryCreatedEvent
   *   - `client_updated`             → ClientUpdatedEvent
   *   - `end_customer_updated`       → EndCustomerUpdatedEvent
   *   - `invoice_attachment_created` → InvoiceAttachmentCreatedEvent
   *   - `invoice_attachment_updated` → InvoiceAttachmentUpdatedEvent
   *   - `invoice_updated`            → InvoiceUpdatedEvent
   *   - `comment_changed`            → CommentChangedEvent
   *   - `invoice_presence_changed`   → InvoicePresenceChangedEvent
   *   - `invoice_workflow_activity`  → InvoiceWorkflowActivityEvent
   *   - `workflow_status`            → WorkflowStatusEvent
   *   - `export_changed`             → ExportChangedEvent
   *   - `extraction_changed`         → ExtractionChangedEvent
   *
   * OpenAPI cannot model `text/event-stream` as a typed channel, so
   * the response schema stays `{ type: string }`. Clients SHOULD
   * discriminate on `event_type` and SHOULD gracefully ignore unknown
   * values to remain forward-compatible.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `subscribeNotifications$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  subscribeNotifications(params: {
    page_instance_id: string;
    context?: HttpContext
  }
): Observable<string> {

    return this.subscribeNotifications$Response(params).pipe(
      map((r: StrictHttpResponse<string>) => r.body as string)
    );
  }

  /**
   * Path part for operation addNotificationSubscription
   */
  static readonly AddNotificationSubscriptionPath = '/notifications/subscriptions';

  /**
   * Attach an entity subscription to an open SSE connection.
   *
   * Console calls this on navigation into an invoice / client /
   * end-customer page so the corresponding SSE events flow to the tab.
   * The connection identified by `page_instance_id` must already exist
   * (i.e. an SSE GET /notifications/subscribe is open) and belong to
   * the caller; otherwise the answer is 404.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addNotificationSubscription()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addNotificationSubscription$Response(params: {
    context?: HttpContext
    body: AddSubscriptionRequest
  }
): Observable<StrictHttpResponse<AddSubscriptionResponse>> {

    const rb = new RequestBuilder(this.rootUrl, NotificationsService.AddNotificationSubscriptionPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<AddSubscriptionResponse>;
      })
    );
  }

  /**
   * Attach an entity subscription to an open SSE connection.
   *
   * Console calls this on navigation into an invoice / client /
   * end-customer page so the corresponding SSE events flow to the tab.
   * The connection identified by `page_instance_id` must already exist
   * (i.e. an SSE GET /notifications/subscribe is open) and belong to
   * the caller; otherwise the answer is 404.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `addNotificationSubscription$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addNotificationSubscription(params: {
    context?: HttpContext
    body: AddSubscriptionRequest
  }
): Observable<AddSubscriptionResponse> {

    return this.addNotificationSubscription$Response(params).pipe(
      map((r: StrictHttpResponse<AddSubscriptionResponse>) => r.body as AddSubscriptionResponse)
    );
  }

  /**
   * Path part for operation removeNotificationSubscription
   */
  static readonly RemoveNotificationSubscriptionPath = '/notifications/subscriptions/{subscriptionID}';

  /**
   * Release an entity subscription.
   *
   * Acts only on a subscription of one of the caller's own
   * connections; another user's subscription answers 404, as an
   * unknown id does.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `removeNotificationSubscription()` instead.
   *
   * This method doesn't expect any request body.
   */
  removeNotificationSubscription$Response(params: {
    subscriptionID: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, NotificationsService.RemoveNotificationSubscriptionPath, 'delete');
    if (params) {
      rb.path('subscriptionID', params.subscriptionID, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Release an entity subscription.
   *
   * Acts only on a subscription of one of the caller's own
   * connections; another user's subscription answers 404, as an
   * unknown id does.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `removeNotificationSubscription$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  removeNotificationSubscription(params: {
    subscriptionID: string;
    context?: HttpContext
  }
): Observable<void> {

    return this.removeNotificationSubscription$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation updateNotificationSubscription
   */
  static readonly UpdateNotificationSubscriptionPath = '/notifications/subscriptions/{subscriptionID}';

  /**
   * Update the activity_type of an existing invoice subscription.
   *
   * Acts only on a subscription of one of the caller's own
   * connections; another user's subscription answers 404, as an
   * unknown id does.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateNotificationSubscription()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateNotificationSubscription$Response(params: {
    subscriptionID: string;
    context?: HttpContext
    body: UpdateSubscriptionRequest
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, NotificationsService.UpdateNotificationSubscriptionPath, 'patch');
    if (params) {
      rb.path('subscriptionID', params.subscriptionID, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Update the activity_type of an existing invoice subscription.
   *
   * Acts only on a subscription of one of the caller's own
   * connections; another user's subscription answers 404, as an
   * unknown id does.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateNotificationSubscription$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateNotificationSubscription(params: {
    subscriptionID: string;
    context?: HttpContext
    body: UpdateSubscriptionRequest
  }
): Observable<void> {

    return this.updateNotificationSubscription$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getInvoicePresence
   */
  static readonly GetInvoicePresencePath = '/invoices/{invoiceID}/presence';

  /**
   * Return the current roster of users subscribed to this invoice.
   *
   * The roster is updated server-side via the `invoice_presence_changed`
   * SSE event whenever someone joins, leaves, or changes their
   * `activity_type`. Console fetches this endpoint once on page mount
   * to seed the displayed roster, then updates on each event.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getInvoicePresence()` instead.
   *
   * This method doesn't expect any request body.
   */
  getInvoicePresence$Response(params: {
    invoiceID: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<InvoicePresence>> {

    const rb = new RequestBuilder(this.rootUrl, NotificationsService.GetInvoicePresencePath, 'get');
    if (params) {
      rb.path('invoiceID', params.invoiceID, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<InvoicePresence>;
      })
    );
  }

  /**
   * Return the current roster of users subscribed to this invoice.
   *
   * The roster is updated server-side via the `invoice_presence_changed`
   * SSE event whenever someone joins, leaves, or changes their
   * `activity_type`. Console fetches this endpoint once on page mount
   * to seed the displayed roster, then updates on each event.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getInvoicePresence$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getInvoicePresence(params: {
    invoiceID: number;
    context?: HttpContext
  }
): Observable<InvoicePresence> {

    return this.getInvoicePresence$Response(params).pipe(
      map((r: StrictHttpResponse<InvoicePresence>) => r.body as InvoicePresence)
    );
  }

}
